import { SquareIcon } from '@phosphor-icons/react';
import {
  Table,
  TableRow,
  TableHeader,
  TableBody,
  TableHeaderCell,
  TableCell,
  Text,
  List,
  Stack,
  useSpace,
} from 'braid-design-system';
import { SmartTextLink, InlineCode } from 'scoobie';

export const ApplyWithSeekChecklist = () => {
  const { grid } = useSpace();

  return (
    <Table label="Apply with SEEK & Ad Performance Panel checklist">
      <TableHeader>
        <TableRow>
          <TableHeaderCell minWidth={grid * 40} wrap>
            <Text>Build element</Text>
          </TableHeaderCell>
          <TableHeaderCell minWidth={grid * 60} wrap>
            <Text>Acceptance criteria</Text>
          </TableHeaderCell>
        </TableRow>
      </TableHeader>
      <TableBody>
        <TableRow>
          <TableCell wrap>
            <Text>
              <SmartTextLink href="/use-cases/apply-with-seek/handle-authorization">
                Authentication via SEEK API
              </SmartTextLink>
            </Text>
          </TableCell>
          <TableCell wrap>
            <List icon={<SquareIcon />} type="icon">
              <Text>Authenticate with OAuth 2.0 client credentials</Text>
              <Text>Retrieve partner token to use Apply with SEEK</Text>
            </List>
          </TableCell>
        </TableRow>
        <TableRow>
          <TableCell wrap>
            <Text>
              <SmartTextLink href="/use-cases/apply-with-seek/send-application-complete-signal#handling-multiple-seek-tokens">
                Link-out tracking
              </SmartTextLink>
            </Text>
          </TableCell>
          <TableCell wrap>
            <List icon={<SquareIcon />} type="icon">
              <Text>
                Accurately track the source of application and pass along all
                necessary parameters
              </Text>
            </List>
          </TableCell>
        </TableRow>
        <TableRow>
          <TableCell wrap>
            <Text>
              <SmartTextLink href="/use-cases/apply-with-seek/display-button">
                Display the localised Apply with SEEK button
              </SmartTextLink>
            </Text>
          </TableCell>
          <TableCell wrap>
            <List icon={<SquareIcon />} type="icon">
              <Text>
                Display the Apply with SEEK button for all positions advertised
                on SEEK, regardless of whether the candidate arrived from SEEK
                or some other source
              </Text>
              <Text>
                Display the Apply with SEEK button for positions not advertised
                on SEEK (optional)
              </Text>
              <Text>
                Dynamically display the correct button image and localised label
                (Apply with SEEK / Jobstreet / Jobsdb)
              </Text>
              <Text>
                Display the button in an appropriate place on your apply form
              </Text>
            </List>
          </TableCell>
        </TableRow>
        <TableRow>
          <TableCell wrap>
            <Text>
              <SmartTextLink href="/use-cases/apply-with-seek/handle-authorization">
                Handle SEEK candidate authorization
              </SmartTextLink>
            </Text>
          </TableCell>
          <TableCell wrap>
            <List icon={<SquareIcon />} type="icon">
              <Text>
                Navigate candidates to the authorization page when they click on
                the Apply with SEEK button
              </Text>
              <Text>
                Track and pass along parameters from the authorization flow
              </Text>
            </List>
          </TableCell>
        </TableRow>
        <TableRow>
          <TableCell wrap>
            <Text>
              <SmartTextLink href="/use-cases/apply-with-seek/retrieve-seek-profile">
                Retrieve profile information
              </SmartTextLink>{' '}
              and{' '}
              <SmartTextLink href="/use-cases/apply-with-seek/download-resume">
                download attachments
              </SmartTextLink>
            </Text>
          </TableCell>
          <TableCell wrap>
            <Stack space="medium">
              <Text>
                The following criteria will be visually validated in the next
                step:
              </Text>
              <List icon={<SquareIcon />} type="icon">
                <Text>
                  Query only for the candidate profile fields that will be used
                  to pre-fill your apply form
                </Text>
                <Text>
                  Identify the type of file attachments from query results
                </Text>
                <Text>
                  If an error occurs with the{' '}
                  <InlineCode>candidateProfile</InlineCode> query or attachment
                  downloads, present a new Apply with SEEK button on your apply
                  form to allow the candidate to retry
                </Text>
              </List>
            </Stack>
          </TableCell>
        </TableRow>
        <TableRow>
          <TableCell wrap>
            <Text>
              <SmartTextLink href="/use-cases/apply-with-seek/populate-form">
                Pre-fill apply form with profile information
              </SmartTextLink>
            </Text>
          </TableCell>
          <TableCell wrap>
            <List icon={<SquareIcon />} type="icon">
              <Text>
                Populate the apply form with all matching fields on the SEEK
                Profile
              </Text>
              <Text>Attach the resume where available</Text>
              <Text>
                Gracefully handle scenarios where some information may be
                unavailable, empty, or may have failed your form’s validation by
                displaying descriptive error messages to the candidate
              </Text>
            </List>
          </TableCell>
        </TableRow>
        <TableRow>
          <TableCell wrap>
            <Text>
              <SmartTextLink href="/use-cases/apply-with-seek/send-application-complete-signal">
                Send application complete signal
              </SmartTextLink>
            </Text>
          </TableCell>
          <TableCell wrap>
            <List icon={<SquareIcon />} type="icon">
              <Text>
                Successfully send the application complete signal when required
              </Text>
              <Text>
                <SmartTextLink href="/use-cases/apply-with-seek/send-application-complete-signal#handling-multiple-seek-tokens">
                  Pass the correct <InlineCode>seek-token</InlineCode> in all
                  cases
                </SmartTextLink>
              </Text>
              <Text>
                <SmartTextLink href="/use-cases/apply-with-seek/send-application-complete-signal#error-handling">
                  Register & track the result of the{' '}
                  <InlineCode>sendSignal</InlineCode> mutation
                </SmartTextLink>
              </Text>
              <Text>
                <SmartTextLink href="/use-cases/apply-with-seek/send-application-complete-signal#error-handling">
                  Retry sending of the application complete signal if your
                  software receives authentication or transient server errors
                </SmartTextLink>
              </Text>
            </List>
          </TableCell>
        </TableRow>
        <TableRow>
          <TableCell wrap>
            <Text>
              <SmartTextLink href="/use-cases/ad-performance/panel">
                Embed Ad Performance Panel
              </SmartTextLink>{' '}
              (optional)
            </Text>
          </TableCell>
          <TableCell wrap>
            <List icon={<SquareIcon />} type="icon">
              <Text>
                <SmartTextLink href="/use-cases/ad-performance/panel#user-experience">
                  Embed the panel on the view in your software that directly
                  corresponds to the specific SEEK job ad
                </SmartTextLink>
              </Text>
              <Text>Accurately present the job attributes in the panel</Text>
              <Text>
                If your software supports multiple hirer accounts,{' '}
                <SmartTextLink href="/use-cases/ad-performance/panel#step-3-handle-browser-token-requests">
                  refresh the panel view when switching to a different hirer
                  account
                </SmartTextLink>
              </Text>
              <Text>Limit viewing to only authorized users</Text>
            </List>
          </TableCell>
        </TableRow>
      </TableBody>
    </Table>
  );
};
